import { Grid } from "@mui/material";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataRecipientLogo } from "../../DataRecipientLogo";
import { ExternalLink } from "../../../../common/components/ExternalLink";
import {
  DocumentationInput,
  DocumentationInputValue,
  useDocumentationInput,
} from "../../get-compliant/vendors/DocumentationInput";
import { Close } from "@mui/icons-material";
import { ApiRouteHelpers } from "../../../../common/root/apiRoutes";

type DataRecipientDocumentationCardProps = {
  organizationId: string;
  recipient: OrganizationDataRecipientDto;
  className?: string;
  logoProps?: React.ComponentProps<typeof DataRecipientLogo>;
  disabled?: boolean;
  onUrlChange: (vendorContractUrl: string, key?: string) => Promise<void>;
  onFileUpload: (fileName: string, fileData: File, key?: string) => Promise<{ fileKey: string }>;
  onFileDelete: (fileKey: string, key?: string) => Promise<void>;
};

export const DataRecipientDocumentationCard: React.FC<DataRecipientDocumentationCardProps> = ({
  organizationId,
  recipient,
  className,
  logoProps = {},
  onUrlChange,
  onFileUpload,
  onFileDelete,
  disabled = false,
}) => {
  const [fileKey, setFileKey] = useState<string>("");
  const [initialValue, setInitialValue] = useState<DocumentationInputValue | null>(null);
  useEffect(() => {
    if (recipient.gdprSccUrl) {
      setInitialValue({ type: "url", url: recipient.gdprSccUrl });
    } else if (recipient.gdprSccFileName) {
      setInitialValue({
        type: "file",
        fileName: recipient.gdprSccFileName,
        fileKey: recipient.gdprSccFileKey,
      });
      if (recipient.gdprSccFileKey) {
        setFileKey(recipient.gdprSccFileKey);
      }
    }
  }, [recipient]);

  const publicDocumentation = recipient.vendorGdprDpfUrl || recipient.vendorGdprSccUrl;

  const {
    value,
    props: inputProps,
    clear: clearUserInput,
    error: documentationError,
    loading,
  } = useDocumentationInput({
    initialValue,
    onChange: async (newValue, prevValue) => {
      if (prevValue?.type == "file" && (newValue?.type != "file" || !newValue.file)) {
        await onFileDelete(prevValue.fileKey);
        setFileKey("");
      }
      if (newValue?.type == "file" && newValue.file) {
        const res = await onFileUpload(newValue.fileName, newValue.file);
        if (res?.fileKey) {
          setFileKey(res.fileKey);
        }
      }
      if (prevValue?.type == "url" && newValue?.type != "url") {
        await onUrlChange("");
      }
      if (newValue?.type == "url") {
        await onUrlChange(newValue.url);
      }
    },
  });

  return (
    <Grid
      container
      item
      justifyContent="space-between"
      alignItems="flex-start"
      xs={12}
      sm={6}
      key={`data-recipient-documentation-${recipient.vendorId}`}
      className={`${className} ${loading ? "opacity-50" : ""}`}
    >
      <div className="p-md border-color-neutral-300 box-rounded fill d-flex align-items-center flex--justify-start h-115">
        <DataRecipientLogo
          dataRecipient={recipient}
          size={32}
          {...logoProps}
          className={clsx(logoProps.className, "mr-md")}
        />

        <div className="w-256">
          <div className="text-t2 text-weight-bold line-height-normal">{recipient.name}</div>
          {publicDocumentation && (
            <ExternalLink
              icon={true}
              className="text-t1 text-weight-medium"
              href={publicDocumentation}
            >
              View public documentation
            </ExternalLink>
          )}
          {value?.type == "url" && value.url && (
            <div className="d-flex align-items-start">
              <ExternalLink
                icon={true}
                className="text-t1 text-weight-medium"
                href={`https://${value.url.replace(/^https?:\/\//, "")}`}
              >
                <span className="max-width-192 text-ellipsis">
                  {value.url.replace(/^https?:\/\//, "")}
                </span>
              </ExternalLink>
              <Close
                fontSize="inherit"
                className="ml-xxs mt-xxxs cursor-pointer"
                onClick={() => clearUserInput()}
              />
            </div>
          )}
          {value?.type == "file" && fileKey && (
            <div className="d-flex align-items-start">
              <ExternalLink
                icon={false}
                className="text-t1 text-weight-medium"
                href={ApiRouteHelpers.storage.vendorAgreements.byType(
                  organizationId,
                  recipient.vendorId,
                  "scc",
                )}
              >
                <span className="max-width-192 text-ellipsis">{value.fileName}</span>
              </ExternalLink>
              <Close
                fontSize="inherit"
                className="ml-xxs mt-xxxs cursor-pointer"
                onClick={() => clearUserInput()}
              />
            </div>
          )}
          {documentationError?.type == "file" && (
            <div className="text-t1 color-red-600 line-height-normal">
              {documentationError.error}
            </div>
          )}
        </div>

        {(!value?.type || (value?.type == "url" && !value.url) || Boolean(documentationError)) && (
          <div className="flex-grow flex-container flex-end">
            <DocumentationInput variant="popover" {...inputProps} disabled={disabled || loading} />
          </div>
        )}
      </div>
    </Grid>
  );
};
